import { useState } from 'react';
import { Button, Badge, Dialog, Input, Select, Alert, ConfirmDialog } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, StatusBadge, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import DataTable from '../components/DataTable';
import { formatDateTime } from '../lib/format';
import { usePlatformAdmin, usePlatformCan } from '../lib/access';

/**
 * Platform admins: invite by one-time join link (7 days), change role, disable / enable.
 * Access is granted by the role's permissions, never by its name. Two-factor sign-in
 * is not enrolled yet (D23), so MFA shows as off.
 */
export default function Admins() {
  const me = usePlatformAdmin();
  const canManage = usePlatformCan('admins.manage');
  const query = useQuery('platformAdmins');
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [link, setLink] = useState(null);
  const [msg, setMsg] = useState('');
  const status = useMutation('platformAdminStatus');

  const replace = (row) => query.setData((b) => ({ ...b, data: b.data.some((a) => a.id === row.id) ? b.data.map((a) => (a.id === row.id ? row : a)) : [...b.data, row] }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform admins"
        subtitle="Separately audited operator accounts; access is granted by permission, not role name"
        actions={canManage ? <Button onClick={() => setInviting(true)}>Invite admin</Button> : null}
      />
      {msg ? <Alert variant="success" title={msg} dismissible onDismiss={() => setMsg('')} /> : null}
      {link ? (
        <Alert variant="info" title={`Join link for ${link.email}`} dismissible onDismiss={() => setLink(null)}>
          <div className="space-y-1 text-sm">
            <code className="block p-2 rounded bg-[var(--app-surface)] select-all break-all">{link.url}</code>
            <div>{link.mailed ? 'It was emailed too. ' : ''}It works once, for 7 days; inviting again replaces it.</div>
          </div>
        </Alert>
      ) : null}
      <QueryState query={query}>
        {(body) => (
          <>
            <SectionCard title="Admins">
              <DataTable
                data={body.data}
                columns={[
                  { key: 'name', label: 'Admin', render: (a) => <div><div className="font-medium">{a.name}{a.email === me.email ? ' (you)' : ''}</div><div className="text-xs opacity-60">{a.email}</div></div> },
                  { key: 'role', label: 'Role' },
                  { key: 'mfa', label: 'MFA', hideOnMobile: true, render: (a) => <Badge variant={a.mfa ? 'success' : 'warning'} size="sm">{a.mfa ? 'On' : 'Off'}</Badge> },
                  { key: 'status', label: 'Status', render: (a) => <StatusBadge status={a.status} /> },
                  { key: 'last_login_at', label: 'Last sign-in', hideOnMobile: true, render: (a) => formatDateTime(a.last_login_at) },
                  ...(canManage ? [{
                    key: 'actions', label: '', sortable: false, render: (a) => (a.email === me.email ? null : (
                      <div className="flex flex-wrap justify-end gap-1">
                        {a.status !== 'disabled' ? <Button size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); setEditing(a); }}>Role</Button> : null}
                        {a.status === 'invited' ? <Button size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); setInviting(a); }}>Re-send</Button> : null}
                        <Button size="xs" variant={a.status === 'disabled' ? 'success' : 'ghost'} onClick={(e) => { e.stopPropagation(); setToggling(a); }}>
                          {a.status === 'disabled' ? 'Enable' : 'Disable'}
                        </Button>
                      </div>
                    )),
                  }] : []),
                ]}
              />
              <p className="text-xs opacity-60">Two-factor sign-in is not enrolled yet (backlog); every admin's MFA shows as off until it is.</p>
            </SectionCard>
            <SectionCard title="Role permissions">
              <div className="grid gap-3 md:grid-cols-2">
                {body.roles.map((r) => (
                  <div key={r.key} className="rounded-md border border-[var(--app-border)] p-3 space-y-2">
                    <div className="font-medium">{r.label}</div>
                    <div className="flex flex-wrap gap-1">{r.permissions.map((p) => <Badge key={p} variant="outline" size="sm">{p === '*' ? 'all permissions' : p}</Badge>)}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
            {inviting ? (
              <InviteDialog
                roles={body.roles}
                resend={inviting === true ? null : inviting}
                onClose={() => setInviting(false)}
                onInvited={(row) => {
                  const { join_url: url, mailed, ...admin } = row;
                  replace(admin);
                  setLink({ email: row.email, url, mailed });
                  setMsg(mailed ? `Invitation sent to ${row.email}` : `Invitation created for ${row.email}; email is not configured, so share the link below`);
                }}
              />
            ) : null}
            {editing ? (
              <RoleDialog
                admin={editing}
                roles={body.roles}
                onClose={() => setEditing(null)}
                onSaved={(row) => { replace(row); setMsg(`${row.email} is now ${row.role}`); }}
              />
            ) : null}
            {toggling ? (
              <ConfirmDialog
                isOpen
                title={`${toggling.status === 'disabled' ? 'Enable' : 'Disable'} ${toggling.email}?`}
                description={toggling.status === 'disabled' ? 'They can sign in again with their password.' : 'Their open sessions end now and they cannot sign in until enabled again.'}
                confirmLabel={toggling.status === 'disabled' ? 'Enable' : 'Disable'}
                destructive={toggling.status !== 'disabled'}
                loading={status.loading}
                onCancel={() => setToggling(null)}
                onConfirm={async () => {
                  try {
                    const r = await status.run({ id: toggling.id, status: toggling.status === 'disabled' ? 'active' : 'disabled' });
                    replace(r.data);
                    setMsg(`${r.data.email} ${r.data.status === 'disabled' ? 'disabled' : 'enabled'}`);
                  } catch {
                    setMsg('');
                  }
                  setToggling(null);
                }}
              />
            ) : null}
            {status.error ? <Alert variant="error" title="Not changed">{status.error}</Alert> : null}
          </>
        )}
      </QueryState>
    </div>
  );
}

function InviteDialog({ roles, resend, onClose, onInvited }) {
  const invite = useMutation('platformAdminInvite');
  const [form, setForm] = useState(resend ? { name: resend.name, email: resend.email, role: resend.role_key } : { name: '', email: '', role: '' });
  const submit = async () => {
    try {
      const r = await invite.run(form);
      onInvited(r.data);
      onClose();
    } catch {
      /* invite.error shown below */
    }
  };
  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={resend ? `Re-send invitation to ${resend.email}` : 'Invite platform admin'}
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={invite.loading} disabled={!form.email || !form.role}>{resend ? 'Send a new link' : 'Send invite'}</Button>
        </div>
      )}
    >
      <div className="space-y-3">
        {invite.error ? <Alert variant="error" title="Not invited">{invite.error}</Alert> : null}
        <Input label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <Input label="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required disabled={!!resend} />
        <Select label="Role" options={roles.map((r) => ({ value: r.key, label: r.label }))} value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))} closeOnSelect required />
        <p className="text-xs opacity-60">The invitee sets their own password from the link. Two-factor enrolment comes later (D23).</p>
      </div>
    </Dialog>
  );
}

function RoleDialog({ admin, roles, onClose, onSaved }) {
  const save = useMutation('platformAdminRole');
  const [role, setRole] = useState(admin.role_key || '');
  const submit = async () => {
    try {
      const r = await save.run({ id: admin.id, role });
      onSaved(r.data);
      onClose();
    } catch {
      /* save.error shown below */
    }
  };
  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={`Role for ${admin.email}`}
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={save.loading} disabled={!role || role === admin.role_key}>Save role</Button>
        </div>
      )}
    >
      <div className="space-y-3">
        {save.error ? <Alert variant="error" title="Not changed">{save.error}</Alert> : null}
        <Select label="Role" options={roles.map((r) => ({ value: r.key, label: r.label }))} value={role} onChange={setRole} closeOnSelect required />
        <div className="flex flex-wrap gap-1">{((roles.find((r) => r.key === role) || {}).permissions || []).map((p) => <Badge key={p} variant="outline" size="sm">{p === '*' ? 'all permissions' : p}</Badge>)}</div>
        <p className="text-xs opacity-60">Takes effect on their next request.</p>
      </div>
    </Dialog>
  );
}
